import { SubmissionError, reset } from 'redux-form';
import Auth from '@aws-amplify/auth';
import { auth } from 'api';
import * as constants from 'common/constants';

export const loginSuccess = payload => ({
  type: constants.LOGIN_SUCCESS,
  payload,
});

// Expects an object with members 'code' and 'message'
export const mapLoginErrors = error => {
  let errorObject = {};

  switch (error.code) {
    // If the e-mail used doesn't exist or the password used is wrong for the
    // given e-mail, show a generic error message describing either case.
    case constants.LOGIN_ERRORS.USER_NOT_FOUND:
    case constants.LOGIN_ERRORS.USER_NOT_AUTHORIZED:
      errorObject = {
        username: true,
        password: true,
        _error:
          'The email or password you entered did not match our records. Please try again.',
      };
      break;
    case constants.LOGIN_ERRORS.USER_NOT_CONFIRMED:
      errorObject['_error'] = error.message;
      break;
    default:
      // TODO: temp fix for `mixpanel` error
      if (error.message && error.message.indexOf('given_name') < 0) {
        errorObject['_error'] = error.message;
      }
      break;
  }

  return errorObject;
};

export const getInvalidParameterError = message => {
  let error = message;
  // Matches text like:
  // 1 validation error detected: Value at 'password' failed to satisfy constraint: Member must have length greater than or equal to 6
  const lengthError = /password.*length\sgreater/i.test(error);

  if (lengthError) {
    error = 'Password must be at least 8 characters in length.';
  } else {
    const policyError = error.match(
      /Password did not conform with policy: (.*)/i
    );
    if (policyError) {
      error = policyError[1];
    }
  }

  return error;
};

export const mapRegisterErrors = error => {
  const errorObject = {};

  switch (error.code) {
    case constants.REGISTER_ERRORS.USERNAME_EXISTS:
      errorObject['_error'] = error.message;
      break;
    case constants.REGISTER_ERRORS.INVALID_PASSWORD:
      errorObject['_error'] = getInvalidParameterError(error.message);
      break;
    // this is for parameter, but farmSize is the one has limitation
    case constants.REGISTER_ERRORS.INVALID_PARAMETER:
      errorObject['_error'] = getInvalidParameterError(error.message);
      break;
    case constants.REGISTER_ERRORS.TERMS_NOT_ACCEPTED:
      errorObject['_error'] = error.message;
      break;
    default:
      errorObject['_error'] = error.message;
  }

  return errorObject;
};

export const login = ({ username, password }) => async dispatch => {
  dispatch({ type: constants.LOGIN_PENDING });

  try {
    // swap the email value to lowercase to avoid aws case sensitive error
    const authInfo = await auth.login(
      username && username.toLowerCase(),
      password
    );

    try {
      const currentUser = await Auth.currentUserInfo(authInfo);

      dispatch(loginSuccess(currentUser));
      dispatch(reset('login'));
    } catch (error) {
      throw new SubmissionError(mapLoginErrors(error));
    }
  } catch (error) {
    // Email to the user confirmation, and redirect to ConfirmEmail page
    if (error.code === constants.LOGIN_ERRORS.USER_NOT_CONFIRMED) {
      Auth.resendSignUp(username);
      dispatch({
        type: constants.REGISTER_SUCCESS,
        payload: { user: { username } },
      });
    }

    dispatch({ type: constants.LOGIN_FAILED, payload: { error } });
    throw new SubmissionError(mapLoginErrors(error));
  }
};

export const register = ({
  termsAgreed,
  email,
  ...values
}) => async dispatch => {
  dispatch({ type: constants.REGISTER_PENDING });

  try {
    if (!termsAgreed) {
      throw {
        code: 'TermsNotAccepted',
        message:
          'You are required to accept the terms before creating your account.',
      };
    } else {
      // Backend wants a string value of 1 or 0
      termsAgreed = '1';
    }

    // swap the email value to lowercase to avoid aws case sensitive error
    const authInfo = await auth.register({
      termsAgreed,
      email: email && email.toLowerCase(),
      ...values,
    });
    dispatch({
      type: constants.REGISTER_SUCCESS,
      payload: { ...authInfo, meta: { ...values } },
    });

    return true;
  } catch (e) {
    dispatch({ type: constants.REGISTER_FAILED, payload: e });
    throw new SubmissionError(mapRegisterErrors(e));
  }
};

export const logout = () => async dispatch => {
  dispatch({ type: constants.LOGOUT_PENDING });
  try {
    dispatch({
      type: '@@router/LOCATION_CHANGE',
      payload: { action: 'PUSH', location: { pathname: '/' } },
    });
    auth
      .logout()
      .then(() => {
        dispatch({ type: constants.LOGOUT_SUCCESS });
      })
      .catch(err => {
        throw err;
      });
  } catch (e) {
    dispatch({ type: constants.LOGOUT_FAILED, payload: e });
  }
};

export const forgotPassword = ({ forgotPasswordEmail }) => async dispatch => {
  forgotPasswordEmail =
    forgotPasswordEmail && forgotPasswordEmail.toLowerCase();
  dispatch({
    type: constants.FORGOT_PASSWORD_PENDING,
    payload: forgotPasswordEmail,
  });

  try {
    await auth.forgotPassword(forgotPasswordEmail);
    dispatch({ type: constants.FORGOT_PASSWORD_SUCCESS });
    return true;
  } catch (e) {
    switch (e.code) {
      case constants.LOGIN_ERRORS.USER_NOT_FOUND:
        // We don't want to inform the user that the entered email doesn't exist
        // So just move to the next step anyway
        dispatch({ type: constants.FORGOT_PASSWORD_SUCCESS });
        return true;
      default:
        // The type of error we expect here is a server problem, so just display
        // a generic error
        dispatch({
          type: constants.FORGOT_PASSWORD_FAILED,
          payload: { errorMessage: e.message || e },
        });
        throw new SubmissionError({ _error: e.message });
    }
  }
};

const mapResetPasswordErrors = error => {
  let errorObject = {};

  switch (error.code) {
    case constants.RESET_PASSWORD_ERRORS.CODE_MISMATCH:
      errorObject['resetPasswordCode'] =
        'Invalid password reset code, please try again';
      break;
    case constants.REGISTER_ERRORS.INVALID_PARAMETER: {
      const message = getInvalidParameterError(error.message);
      errorObject = {
        resetPassword: message,
        confirmResetPassword: message,
      };
      break;
    }
    default:
      errorObject['_error'] = error.message;
  }

  return errorObject;
};

export const resetPassword = ({ resetPasswordCode, resetPassword }) => async (
  dispatch,
  getState
) => {
  dispatch({ type: constants.RESET_PASSWORD_PENDING });

  try {
    const email = getState().auth.email;
    await auth.resetPassword(email, resetPasswordCode, resetPassword);
    dispatch({ type: constants.RESET_PASSWORD_SUCCESS });
    return true;
  } catch (e) {
    dispatch({
      type: constants.RESET_PASSWORD_FAILED,
      payload: { errorMessage: e.message || e },
    });
    throw new SubmissionError(mapResetPasswordErrors(e));
  }
};



// WEBPACK FOOTER //
// ./src/actions/auth.js